import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Award, Target } from 'lucide-react';

/**
 * Case Studies — exact content/screenshots from www.agseosydney.com.au
 * Featured Success Story (Hoselink) + grid of other case studies.
 */
const featured = {
  brand: 'HOSELINK',
  featured: true,
  tenure: '9+ Years',
  description: "Australia's leading garden hose and watering equipment retailer",
  keyword: 'Solar Lights Australia',
  ranking: [
    { site: 'Hoselink', position: '#1' },
    { site: 'Bunnings', position: '#2' },
  ],
  highlight: 'We ranked Hoselink higher than Bunnings',
  image: '/assets/images/cases/hoselink-results.png',
  stats: [
    { value: '201', label: 'Keywords on Page #1' },
    { value: '278%', label: 'Traffic Increase' },
  ],
  topKeywords: [
    'best garden hose',
    'retractable hose reel',
    'garden hose reels australia',
    'hose reel australia',
    'automatic water bowl',
    'hose fitting australia',
  ],
};

const others = [
  {
    brand: 'DISPLAYME',
    description: 'Premium display solutions and signage products for businesses',
    keyword: 'snap frames Sydney',
    image: '/assets/images/cases/snap-frames.png',
    logo: '/assets/images/cases/displayme-logo.png',
    stats: [
      { value: '178', label: 'Keywords on Page #1' },
      { value: '80%', label: 'Traffic Increase' },
    ],
    topKeywords: [
      'snap frames Sydney',
      'real estate window displays',
      'window display systems',
      'brochure stand',
      'light box display',
      'poster frames online',
    ],
  },
  {
    brand: 'LASH BLOSSOM',
    description: 'Premium eyelash extensions and beauty training courses',
    keyword: 'eyelash extensions Sydney',
    image: '/assets/images/cases/lashblossom.png',
    stats: [
      { value: '191', label: 'Keywords on Page #1' },
      { value: '79%', label: 'Traffic Increase' },
    ],
    topKeywords: [
      'eyelash extensions Sydney',
      'eyelash extension',
      'mink eyelash extensions Sydney',
      'microblading course',
      'eyelash extension course',
    ],
  },
  {
    brand: 'STYLE STATE',
    description: 'Wholesale fashion and clothing distributor in Australia',
    keyword: 'wholesale dresses australia',
    image: '/assets/images/cases/stylestate.png',
    stats: [
      { value: '106', label: 'Keywords on Page #1' },
      { value: '25%', label: 'Traffic Increase' },
    ],
    topKeywords: [
      'wholesale dresses australia',
      'wholesale dresses',
      'australia clothing wholesale',
      'wholesale jackets',
      'wholesale fashion',
    ],
  },
  {
    brand: 'CHATIME',
    description: "World's largest bubble tea franchise with 1000+ stores globally",
    keyword: 'premium milk bubble tea',
    image: '/assets/images/cases/chatime-results.png',
    logo: '/assets/images/cases/chatime-logo.png',
    stats: [
      { value: '91', label: 'Keywords on Page #1' },
      { value: '43%', label: 'Traffic Increase' },
    ],
    topKeywords: [
      'premium pearl milk tea',
      'bubble tea franchise australia',
      'bubble tea franchise sydney',
      'milk tea franchise',
      'tea franchise sydney',
      'best bubble tea franchise',
    ],
  },
  {
    brand: 'BIDETS ONLINE',
    description: "Australia's specialist bidet and bathroom accessories retailer",
    keyword: 'buy bidets online',
    image: '/assets/images/cases/bidets-results.png',
    logo: '/assets/images/cases/bidets-logo.png',
    stats: [
      { value: '94', label: 'Keywords on Page #1' },
      { value: '464%', label: 'Traffic Increase' },
    ],
    topKeywords: [
      'bidet',
      'bidet toilet seat',
      'bidet online',
      'bidet shop',
      'buy bidet online',
      'bidet seats australia',
    ],
  },
  {
    brand: 'VERITAS EVENTS',
    description: 'Full-service event management company in Sydney',
    keyword: 'event management sydney',
    image: '/assets/images/cases/cleaners-sydney.png',
    stats: [
      { value: '68', label: 'Keywords on Page #1' },
      { value: '27%', label: 'Traffic Increase' },
    ],
    topKeywords: [
      'event management companies sydney',
      'event management sydney',
      'event organiser company',
      'event managers',
      'live stream conferences',
      'delegate management services',
    ],
  },
];

const CaseStudyCard = ({ study, isFeatured = false }) => (
  <article
    data-testid={`case-study-${study.brand.toLowerCase().replace(/\s+/g, '-')}`}
    className={`group relative rounded-3xl overflow-hidden border-2 border-slate-200 bg-white hover:border-[#15803d]/50 hover:shadow-2xl hover:-translate-y-1 transition-all ${
      isFeatured ? 'lg:grid lg:grid-cols-5 gap-0' : ''
    }`}
  >
    <div className={`relative overflow-hidden bg-gradient-to-br from-orange-50 via-orange-50 to-orange-100 ${isFeatured ? 'lg:col-span-3 flex items-center justify-center p-6 md:p-10 min-h-[380px] lg:min-h-[560px]' : 'aspect-[16/10]'}`}>
      {study.featured && (
        <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 rounded-full bg-[#db2777] text-white px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider shadow-lg">
          <Award size={12} /> Featured Success Story
        </div>
      )}
      {study.tenure && (
        <div className="absolute top-4 right-4 z-10 inline-flex items-center gap-1.5 rounded-full bg-[#f97316] text-white px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider shadow-lg">
          <Award size={12} /> {study.tenure}
        </div>
      )}
      <img
        src={study.image}
        alt={`${study.brand} case study — ranked #1 on Google${study.keyword ? ` for "${study.keyword}"` : ''}`}
        loading="lazy"
        className={`transition-transform duration-700 group-hover:scale-105 ${
          isFeatured ? 'max-h-[520px] w-auto object-contain drop-shadow-2xl' : 'h-full w-full object-cover object-top'
        }`}
      />
    </div>

    <div className={`p-7 md:p-9 ${isFeatured ? 'lg:col-span-2 flex flex-col justify-between' : ''}`}>
      <div>
        <div className="flex items-center gap-3 mb-4">
          {study.logo && (
            <div className="h-10 flex items-center">
              <img loading="lazy" decoding="async" src={study.logo} alt={`${study.brand} logo — GWM SEO client`} className="h-full object-contain" />
            </div>
          )}
          <h3 className="font-['Outfit'] text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
            {study.brand}
          </h3>
        </div>
        <p className="text-slate-600 leading-relaxed mb-5">{study.description}</p>

        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 mb-5">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5">
            Target Keyword
          </p>
          <p className="flex items-center gap-2 text-base font-bold text-slate-900">
            <Target size={16} className="text-[#15803d]" />
            <span>&quot;{study.keyword}&quot;</span>
            <span className="ml-auto rounded-full bg-[#15803d] text-white text-xs font-bold px-2.5 py-0.5">
              RANKS #1
            </span>
          </p>
        </div>

        {study.highlight && (
          <div className="rounded-2xl bg-gradient-to-r from-[#15803d]/10 to-[#22c55e]/10 border-l-4 border-[#15803d] p-4 mb-5">
            <p className="text-sm font-bold text-[#166534] leading-snug">&ldquo;{study.highlight}&rdquo;</p>
            {study.ranking && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                {study.ranking.map((r, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-lg bg-white border border-slate-200 px-3 py-2"
                  >
                    <span className="text-sm font-semibold text-slate-700">{r.site}</span>
                    <span className="text-sm font-black text-[#15803d]">{r.position}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-5">
          {study.stats.map((s, i) => (
            <div
              key={i}
              className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-4 text-center"
            >
              <div className="font-['Outfit'] text-3xl md:text-4xl font-black text-[#4ade80] leading-none mb-1">
                {s.value}
              </div>
              <div className="text-[11px] uppercase tracking-wider text-slate-300 font-semibold">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">
            Top Ranking Keywords
          </p>
          <div className="flex flex-wrap gap-1.5">
            {study.topKeywords.slice(0, 6).map((kw, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700"
              >
                <TrendingUp size={10} className="text-[#15803d]" />
                {kw}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </article>
);

const CaseStudies = () => {
  return (
    <section id="real-brands-real-results" className="py-24 md:py-32 bg-[#f8fafc]" data-testid="case-studies">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <p className="inline-flex items-center gap-2 rounded-full bg-[#db2777]/10 text-[#be185d] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.22em] mb-5">
            <span className="h-1.5 w-1.5 rounded-full bg-[#db2777]" />
            Success Stories
          </p>
          <h2 className="font-['Outfit'] text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
            Real Brands.{' '}
            <span className="italic font-serif text-[#15803d] font-medium">Real Results.</span>
          </h2>
          <p className="mt-5 text-lg md:text-xl text-slate-600 leading-relaxed">
            Proof that we rank Australia&apos;s toughest brands on Page #1 of Google — and keep them there.
          </p>
          <div className="mt-6 mx-auto h-1 w-20 rounded-full bg-gradient-to-r from-[#15803d] to-[#22c55e]" />
        </div>

        <div className="mb-8">
          <CaseStudyCard study={featured} isFeatured />
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {others.map((s, i) => (
            <CaseStudyCard key={i} study={s} />
          ))}
        </div>

        <div className="mt-14 text-center">
          <Link
            to="/contact"
            className="group inline-flex items-center gap-2 rounded-full bg-[#db2777] hover:bg-[#be185d] text-white px-10 py-5 font-bold text-lg shadow-xl shadow-[#db2777]/30 transition-all hover:-translate-y-0.5"
          >
            Be Our Next Success Story
            <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
